import { T } from '../styles/tokens';
/**
 * SkillCard — Reusable skill display card with emoji, name, description, category, and tags
 * Used in AdminSkills, MachineSkillsPanel, and SkillInstallDialog
 */

interface SkillCardProps {
  skill: {
    id?: string;
    name: string;
    description: string;
    emoji?: string;
    category?: string;
    tags?: string[];
    version?: string;
    author?: string;
  };
  onClick?: () => void;
  isHoverable?: boolean;
  footer?: React.ReactNode;
}

export function SkillCard({ skill, onClick, isHoverable = true, footer }: SkillCardProps) {
 const emoji = skill.emoji || '';
  
  return (
    <div
      style={{
        padding: '16px',
        borderRadius: '12px',
        background: T.bgSubtle,
        border: '1px solid var(--border)',
        transition: 'all 0.15s ease',
        cursor: isHoverable ? 'pointer' : 'default',
        ...(isHoverable && { minHeight: '280px', display: 'flex', flexDirection: 'column' }),
      }}
      onClick={onClick}
      onMouseEnter={(e: any) => {
        if (isHoverable) {
          e.currentTarget.style.background = T.accentBg;
          e.currentTarget.style.borderColor = T.accentBg;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e: any) => {
        if (isHoverable) {
          e.currentTarget.style.background = T.bgSubtle;
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Header: Emoji + Name */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
        <span style={{ fontSize: '24px', lineHeight: '1.2' }}>{emoji}</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: '700',
              color: 'var(--text)',
              wordBreak: 'break-word',
              lineHeight: '1.3',
            }}
          >
            {skill.name}
          </div>
          {skill.version && (
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
              v{skill.version}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: '12px',
          color: 'var(--text-muted)',
          lineHeight: '1.5',
          marginBottom: '12px',
          flex: 1,
        }}
      >
        {skill.description}
      </div>

      {/* Category Badge */}
      {skill.category && (
        <div
          style={{
            fontSize: '10px',
            fontWeight: '600',
            padding: '6px 10px',
            borderRadius: '6px',
            background: T.accentBg,
            color: T.purple,
            width: 'fit-content',
            marginBottom: '8px',
          }}
        >
          {skill.category}
        </div>
      )}

      {/* Tags */}
      {skill.tags && skill.tags.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: footer ? '12px' : '0',
          }}
        >
          {skill.tags.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: '9px',
                padding: '4px 8px',
                borderRadius: '4px',
                background: T.primaryBg,
                color: T.primary,
                border: `1px solid ${T.primaryBgStrong}`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer: Custom content (buttons, status, etc.) */}
      {footer && <div style={{ marginTop: 'auto' }}>{footer}</div>}
    </div>
  );
}
